import { Center } from "@tento-ui/component-builtin";
import { $qs } from "@tento-ui/dom-selectors";
import { Button } from "@tento-ui/components/button/button";
import { PropertyList } from "@tento-ui/components/input/property-list";
import { Storybook } from "@tento-ui/ui/storybook";
import { Typography } from "@tento-ui/ui/typography";
import { Round } from "@tento-ui/components/round";
import { ScrollVertical } from "./scroll-vertical";

export class ScrollVerticalStoryBook extends Storybook.Default {
  title(): string {
    return "Scroll Vertical";
  }

  tags(): string[] {
    return ["std-scroll-vertical"];
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Scroll Vertical",
        render: () => {
          const $c = ScrollVertical.make();
          $c.initialize({
            onCreate: () => {
              return Center.makeWith(
                Round.makeWithContent(
                  Center.makeWith(Typography.p("0")).assignStyles({
                    height: "100%",
                    width: "100%",
                  }),
                  {
                    radius: 12,
                    bg: "var(--blue)",
                    continuous: {
                      smoothing: 0.6,
                    },
                  },
                ).assignStyles({
                  height: "100%",
                }),
              ).assignStyles({
                padding: "4px",
              });
            },
            onBind: ($e, index, _heightPx, _resizeOnly) => {
              $qs("p", $e).textContent = String(index);
            },
            onRecycle: ($e) => {
              $qs("p", $e).textContent = "";
            },
          });
          $c.bind({
            cellHeightPx: 120,
            maxIndex: 200,
          });
          return $c.assignStyles({
            height: "300px",
            width: "200px",
            display: "block",
            borderRadius: "8px",
            background: "var(--bg)",
            boxShadow: "var(--box-shadow)",
          });
        },
      },
      {
        title: "Anchor Compensation",
        render: () => {
          // Track per-cell heights; default 80px, cells 0-2 can grow.
          const cellHeights = new Map<number, number>();
          let growCount = 0;

          const $c = ScrollVertical.make();
          const $status = Typography.p("offsetBase: 0");

          $c.initialize({
            onCreate: () => {
              return Center.makeWith(
                Round.makeWithContent(
                  Center.makeWith(Typography.p("0")).assignStyles({
                    height: "100%",
                    width: "100%",
                  }),
                  {
                    radius: 12,
                    bg: "var(--blue)",
                    continuous: { smoothing: 0.6 },
                  },
                ).assignStyles({ height: "100%" }),
              ).assignStyles({ padding: "4px" });
            },
            onBind: ($e, index, _heightPx, _resizeOnly) => {
              const h = cellHeights.get(index) ?? 80;
              $qs("p", $e).textContent = `${index} (${h}px)`;
            },
            onRecycle: ($e) => {
              $qs("p", $e).textContent = "";
            },
            onScroll: () => {
              $status.textContent =
                `offsetBase: ${$c.scrollEngine.window.offsetBase}`;
            },
            options: {
              cellWidthForIndex: (index: number) => {
                return cellHeights.get(index) ?? 80;
              },
            },
          });

          $c.bind({
            cellHeightPx: 80,
            maxIndex: 50,
            initialIndex: 5,
          });

          const growAbove = () => {
            // Make cells 0, 1, 2 taller (simulates content loading above anchor)
            const target = Math.min(growCount, 2);
            const prev = cellHeights.get(target) ?? 80;
            cellHeights.set(target, prev + 200);
            growCount++;
            $c.invalidateHeight(target);
            $status.textContent =
              `offsetBase: ${$c.scrollEngine.window.offsetBase}`;
          };

          return {
            $content: $c.assignStyles({
              height: "300px",
              width: "200px",
              display: "block",
              borderRadius: "8px",
              background: "var(--bg)",
              boxShadow: "var(--box-shadow)",
            }),
            $controls: PropertyList.makeWith({
              grow: {
                label: "Grow Above",
                $e: Button.makeWithLight("Grow Above", {
                  type: "primary",
                  size: "thin",
                  onClick: growAbove,
                }),
                order: 0,
              },
            }),
            $status,
          };
        },
      },
    ];
  }
}
